import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ItemsInOrderTableCoreDepositPopup from '../ItemsInOrderTableCoreDepositPopup';
import {
  removeDecimalsForWonCurrency,
  shouldAddCurrencyInPrice
} from '@app/utils/paymentUtils';
import { ORDER_TYPES } from '@app/constants/commonConstants';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

const ItemsInOrderTablePrice = ({
  coreDeposit,
  currencyNotation = '',
  includeGST,
  taxDisclaimerMessage,
  isCheckFreightSuccessful,
  isCondensedView,
  isQuantityMergeEnabled = true,
  orderType,
  showGST,
  showUnitPriceColumn,
  strikeThrough,
  totalPrice,
  totalPriceWithGST,
  undiscountedPrice,
  unitPrice,
  unitPriceWithGST
}) => {
  const [t] = useTranslation();
  const taxDisclaimerFeatureFlag = useSelector(
    state => state?.featureFlag?.PCC_TaxInformationMessagesFlag
  );
  const eaLabel = t('ITEM_EA_ABB');
  const includesGST = includeGST && !isCheckFreightSuccessful;

  const noNeedKoreanCurrencyFormat = useMemo(
    () => shouldAddCurrencyInPrice(currencyNotation),
    [currencyNotation]
  );

  const getPrice = () => {
    if (orderType === ORDER_TYPES.OFFLINE_ORDER) {
      return noNeedKoreanCurrencyFormat
        ? unitPrice
        : removeDecimalsForWonCurrency(unitPrice);
    } else if (includesGST) {
      // On Pickup and Delivery page, if includeGST comes as true,
      // it should display price with GST tax until taxAndFreight call is successful
      return noNeedKoreanCurrencyFormat
        ? totalPriceWithGST
        : removeDecimalsForWonCurrency(totalPriceWithGST);
    } else {
      return noNeedKoreanCurrencyFormat
        ? totalPrice
        : removeDecimalsForWonCurrency(totalPrice);
    }
  };

  const getUnitPrice = () => {
    const price = includesGST ? unitPriceWithGST : unitPrice;
    return (
      <p className="exclude-global mb-1 text-xs text-univers u-color--DarkGray">
        {`${
          noNeedKoreanCurrencyFormat
            ? price
            : removeDecimalsForWonCurrency(price)
        } ${eaLabel}`}
      </p>
    );
  };

  const GSTExcludes = () => {
    return (
      <>
        {t('GST_EXCLUDES')}
        {!isQuantityMergeEnabled ? (
          <sup>&dagger;&dagger;</sup>
        ) : (
          <sup>&dagger;</sup>
        )}
      </>
    );
  };

  const GSTConditional = includesGST ? t('GST_INCLUDES') : GSTExcludes();

  return (
    <>
      <p className="exclude-global mb-1 text-md text-univers--bold u-color--ExtraDarkGray">
        {getPrice()}{' '}
        {noNeedKoreanCurrencyFormat &&
          !!currencyNotation?.length &&
          isCondensedView && (
            <span className="u-color--ExtraDarkGray">({currencyNotation})</span>
          )}
      </p>
      {strikeThrough && undiscountedPrice && (
        <p className="exclude-global mb-1 text-xs text-univers u-color--DarkGray text-line-through">
          {`${undiscountedPrice} ${eaLabel}`}
        </p>
      )}
      {!showUnitPriceColumn &&
        !isCondensedView &&
        orderType !== ORDER_TYPES.OFFLINE_ORDER &&
        getUnitPrice()}
      {isCondensedView && showGST && (
        <p className="exclude-global mb-1 text-xs text-univers u-color--DarkGray">
          {taxDisclaimerFeatureFlag ? taxDisclaimerMessage : GSTConditional}
        </p>
      )}
      {coreDeposit && (
        <>
          <ItemsInOrderTableCoreDepositPopup />
          {isCondensedView ? (
            <span className="text-xs m-0 ml-1 ">{coreDeposit}</span>
          ) : (
            <p className="exclude-global mb-0 text-xs text-univers u-color--DarkGray">
              {coreDeposit}
            </p>
          )}
        </>
      )}
    </>
  );
};

ItemsInOrderTablePrice.propTypes = {
  coreDeposit: PropTypes.string,
  currencyNotation: PropTypes.string,
  includeGST: PropTypes.bool,
  taxDisclaimerMessage: PropTypes.string,
  isCheckFreightSuccessful: PropTypes.bool,
  isCondensedView: PropTypes.bool,
  isQuantityMergeEnabled: PropTypes.bool,
  orderType: PropTypes.string,
  showGST: PropTypes.bool,
  showUnitPriceColumn: PropTypes.bool,
  strikeThrough: PropTypes.bool,
  totalPrice: PropTypes.string.isRequired,
  totalPriceWithGST: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  undiscountedPrice: PropTypes.string,
  unitPrice: PropTypes.string.isRequired,
  unitPriceWithGST: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default ItemsInOrderTablePrice;
